import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, ShieldCheck, AlertCircle, Loader2, ArrowRight, KeyRound } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export default function SetupPasscode() {
  const [passcode, setPasscode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    if (passcode.length !== 4) return setError('PIN must be exactly 4 digits.');
    if (passcode !== confirm) return setError('PIN codes do not match. Please try again.');

    try {
      setLoading(true);
      setError('');
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { passcode });
      setTimeout(() => navigate('/dashboard'), 500);
    } catch (err) {
      setError('Failed to save security PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const PinInput = ({ value, onChange, placeholder, autoFocus }) => (
    <div className="relative">
      <input
        type="password"
        maxLength={4}
        required
        autoFocus={autoFocus}
        className="w-full py-5 px-6 bg-stripe-bg border-2 border-stripe-border rounded-2xl text-center text-4xl font-black tracking-[1em] placeholder:text-stripe-border focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all"
        placeholder={placeholder || '0000'}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      />
      {value.length === 4 && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center animate-in zoom-in duration-200">
          <div className="w-2 h-2 rounded-full bg-white" />
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-stripe-dark relative overflow-hidden flex-col justify-between p-14">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full -mr-48 -mt-48 blur-3xl" />

        <div className="relative z-10">
          <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-10 w-auto object-contain brightness-0 invert" />
        </div>

        <div className="relative z-10 space-y-8">
          <div className="w-16 h-16 bg-primary-500/10 border border-primary-500/20 rounded-2xl flex items-center justify-center">
            <KeyRound size={32} className="text-primary-400" />
          </div>
          <div>
            <h2 className="text-4xl font-black text-white tracking-tighter leading-tight mb-4">
              One last step.<br />Secure your<br />account.
            </h2>
            <p className="text-stripe-light text-base font-medium leading-relaxed">
              Your 4-digit PIN adds a critical layer of verification every time you log in, keeping your account safe.
            </p>
          </div>
          <div className="space-y-3">
            {[
              'PIN is never stored in plain text',
              'Required at every login session',
              'Can be updated from your profile',
            ].map(item => (
              <div key={item} className="flex items-center gap-3 text-sm text-white/60 font-medium">
                <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/20 text-[10px] font-bold uppercase tracking-[0.3em] relative z-10">
          © {new Date().getFullYear()} Finova (Pvt) Ltd
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-14 bg-[#f6f9fc] relative">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary-500/5 rounded-full -mr-48 -mt-48 blur-3xl pointer-events-none" />

        <div className="w-full max-w-md space-y-8 relative">
          {/* Mobile logo */}
          <div className="lg:hidden text-center">
            <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-9 w-auto object-contain mx-auto" />
          </div>

          {/* Header */}
          <div>
            <p className="text-[10px] font-black text-primary-500 uppercase tracking-[0.25em] mb-2">Account Security</p>
            <h1 className="text-4xl font-black text-stripe-dark tracking-tighter">Setup your PIN</h1>
            <p className="text-stripe-slate text-sm mt-2 font-medium">
              Choose a 4-digit PIN. This is required to access your account on every login.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200/80 rounded-2xl flex items-start gap-3 animate-in slide-in-from-top-2 duration-300">
              <AlertCircle size={18} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-600 font-semibold">{error}</p>
            </div>
          )}

          {/* Card */}
          <div className="bg-white rounded-[28px] border border-stripe-border shadow-stripe p-8 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* PIN status indicator */}
              <div className="flex items-center gap-4 p-4 bg-stripe-bg rounded-2xl border border-stripe-border">
                <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center shrink-0">
                  <ShieldCheck size={20} className="text-primary-500" />
                </div>
                <div>
                  <p className="text-xs font-black text-stripe-dark">Security Protocol</p>
                  <p className="text-[10px] text-stripe-slate font-medium mt-0.5">2-Step verification is mandatory for all accounts.</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">Set Your PIN</label>
                <PinInput value={passcode} onChange={setPasscode} autoFocus />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">Confirm Your PIN</label>
                <PinInput value={confirm} onChange={setConfirm} />
              </div>

              {/* PIN match indicator */}
              {passcode.length === 4 && confirm.length === 4 && (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold animate-in fade-in duration-300 ${
                  passcode === confirm
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-600 border border-red-200'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${passcode === confirm ? 'bg-green-500' : 'bg-red-500'}`} />
                  {passcode === confirm ? 'PINs match — ready to activate' : 'PINs do not match'}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || passcode.length !== 4 || passcode !== confirm}
                className="w-full py-3.5 bg-stripe-dark text-white font-black text-sm rounded-xl hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-40 group"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : (
                  <>Activate & Continue <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></>
                )}
              </button>
            </form>

            <button
              type="button"
              onClick={logout}
              className="w-full text-xs font-bold text-red-400 hover:text-red-500 transition-colors text-center py-2"
            >
              Cancel & Log Out
            </button>
          </div>

          <p className="text-center text-[10px] font-bold text-stripe-slate/30 uppercase tracking-[0.2em]">
            Protected Account Protocol · Finova Security
          </p>
        </div>
      </div>
    </div>
  );
}
